/* eslint-disable no-console */
import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { from, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, concatMap, switchMap, tap } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { commonConstants } from '../constants/common.constants';

// Documentation: https://www.npmjs.com/package/http-status-codes
import { StatusCodes } from 'http-status-codes';
import { TranslocoService } from '@ngneat/transloco';
import { NotificationService } from '../services/notification.service';

@Injectable()
export class ServerErrorInterceptor implements HttpInterceptor {
  isLogginOut = false;
  logoutCompleted = false;
  refreshTokenInProgress = false;
  tokenRefreshedSource = new Subject();
  tokenRefreshed$ = this.tokenRefreshedSource.asObservable();

  constructor(
    private authService: AuthService,
    private translationService: TranslocoService,
    private notificationService: NotificationService
  ) {
    this.notificationService.logoutMessage$.subscribe((configuration) => {
      if (configuration) {
        this.isLogginOut = true;
        this.logoutCompleted = true;
      }

      this.notificationService.show(configuration);
    });

    this.notificationService.showLogoutMessage$.subscribe((show) => {
      if (this.isLogginOut && !show) {
        this.isLogginOut = false;
        this.logout('');
      }
    });
  }

  manage401(error: HttpErrorResponse) {
    return this.refreshToken().pipe(
      catchError((e) => {
        console.debug('manage401 error', e);
        if (e.status === 401) {
          return this.logout(e);
        }
        return e;
      })
    );
  }

  manage403(error: HttpErrorResponse) {
    console.debug('manage403', error);
    return this.logout(error);
  }

  manageDefaultError(error: any) {
    if (this.logoutCompleted) {
      return of(new HttpResponse({}));
    }

    return throwError(error);
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        switch (error.status) {
          case StatusCodes.UNAUTHORIZED: //401
            const isStillValidSession =
              sessionStorage.getItem(commonConstants.SSO) !== null;
            if (!isStillValidSession) {
              return of(new HttpResponse({}));
            }

            if (error?.error?.message === commonConstants.INACTIVE_USER) {
              return this.logout(error);
            }

            return this.manage401(error).pipe(
              switchMap(() => {
                request = this.addAuthHeader(request);

                if (!this.logoutCompleted) {
                  console.debug(
                    'ServerErrorInterceptor: token updated and retrying',
                    this.logoutCompleted
                  );

                  return next.handle(request);
                } else {
                  return of(new HttpResponse({})).pipe(
                    tap(() => (this.logoutCompleted = false))
                  );
                }
              })
            );
          case StatusCodes.FORBIDDEN: //403
            return this.manage403(error);
          default:
            return this.manageDefaultError(error);
        }
      })
    );
  }

  private logout(error: any) {
    const err =
      error?.error?.message ?? error.statusText ?? commonConstants.UNAUTHORIZED;
    const logoutPromise = this.authService.navigateToLoginPage(true, err);
    return from(logoutPromise).pipe(
      concatMap((response) => of(new HttpResponse())),
      tap(() => (this.logoutCompleted = true))
    );
  }

  private addAuthHeader(request: HttpRequest<any>) {
    const authHeader = this.authService.oauthService.authorizationHeader();
    if (authHeader) {
      return request.clone({
        setHeaders: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          Authorization: authHeader,
        },
      });
    }
    return request;
  }

  private refreshToken(): Observable<any> {
    if (this.refreshTokenInProgress) {
      return new Observable((observer) => {
        this.tokenRefreshed$.subscribe(() => {
          observer.next(undefined);
          observer.complete();
        });
      });
    } else {
      this.refreshTokenInProgress = true;

      return this.authService.refreshToken().pipe(
        tap(() => {
          this.refreshTokenInProgress = false;
          this.tokenRefreshedSource.next(true);
        }),
        catchError(async (error) => {
          this.refreshTokenInProgress = false;

          const err = this.translationService.translate(
            `errors.sessionExpired`
          );
          error.error.message = err;

          return this.logout(error);
        })
      );
    }
  }
}
